import { ITEM_NAMES } from '@/utils/editor.constants'
import botModule from '@/store/modules/bot.module'
import sort from 'ramda/src/sort'
import router, { ROUTE_NAMES } from '@/router'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import answerModule from '@/store/modules/answer.module'
import intentModule from '@/store/modules/intent.module'
import toaster from '@/utils/toaster'
import answerService from '@/services/amio/answer.service'
import conversationModule from '@/store/modules/conversation.module'
import isEmpty from 'ramda/src/isEmpty'
import equals from 'ramda/src/equals'
import botService from '@/services/amio/bot.service'
import channelModule from '@/store/modules/channel.module'
import values from 'ramda/src/values'

class EditorUtils {
  isUnknownAnswer(intentPlatformId) {
    return intentPlatformId === botModule.unknownIntentPlatformId()
  }

  sortAnswerList(answers) {
    return sort((a, b) => a.name.localeCompare(b.name), answers)
  }

  resolveNewItemId(deletedId, list) {
    const deletedIndex = list.findIndex(({ id }) => id === deletedId)
    const followingItem = list[deletedIndex + 1]
    const previousItem = list[deletedIndex - 1]
    if (!followingItem && !previousItem) return null

    return followingItem ? followingItem.id : previousItem.id
  }

  resolveConfirmText(isNewlyCreated, itemType) {
    return isNewlyCreated ?
      `Your ${itemType} is not saved. If you continue, the ${itemType} will get deleted.` :
      'Are you sure you want to leave? Any unsaved changes will be lost.'
  }

  updateRouteByItemId(itemName, params, query) {
    const currentRoute = router.history.current
    const currentBotId = currentRoute.params.botId
    const botChanged = currentBotId !== params.botId
    const storeBotOutOfSync = params.botId && botModule.getBot()?.id !== botModule.resolvePrimaryBot(params.botId)?.id

    if ((botChanged || storeBotOutOfSync) && botModule.syncBotById(params.botId)) {
      channelModule.setChannelList(botModule.getPrimaryBotId(params.botId))
    }

    const { idName, location } = this._getRouteProps(itemName, params, query, currentRoute.name)
    if (!currentRoute.name.startsWith(itemName) && !botChanged) return
    if (currentRoute.params[idName] === params[idName] && !botChanged) return

    // https://stackoverflow.com/questions/62223195/vue-router-uncaught-in-promise-error-redirected-from-login-to-via-a
    // Catch to suppress: Error: Navigation aborted from "/bots/111/xxx/xxx" to "/bots/333/xxx/xxx" via a navigation guard.
    router.replace(location).catch(() => {})
  }

  _getRouteProps(itemName, params, query, currentRouteName) {
    switch (true) {
      case currentRouteName.startsWith(ITEM_NAMES.ANSWER):
        return { idName: 'answerId', location: { name: ROUTE_NAMES.answers, params, query } }
      case currentRouteName.startsWith(ITEM_NAMES.EVENT):
        return { idName: 'eventId', location: { name: ROUTE_NAMES.events, params, query } }
      case currentRouteName.startsWith(ITEM_NAMES.BLOCK):
        return { idName: 'blockId', location: { name: ROUTE_NAMES.blocks, params, query } }
      default:
        throw new Error('not a relevant route for 3 cols pages')
    }
  }

  async updateAnswerByPublished(isPublished, answerId, botId = botModule.getBot().id) {
    const publishAnswerToggleId = BUSY_BUTTONS.PUBLISH_ANSWER + answerId
    busyButtonsModule.setButtonBusy(publishAnswerToggleId, true)
    try {
      const updatedAnswer = await answerService.update(botId, answerId, { published: isPublished })

      const answers = answerModule.getAnswerList()
      const updatedAnswers = answers.map(answer => answer.id === updatedAnswer.id ? updatedAnswer : answer)
      answerModule.setAnswerList(updatedAnswers)

      const answerVariants = answerModule.getAnswerVariantList()
      const updatedAnswerVariants = answerVariants
        .map(answerVariant => answerVariant.id === updatedAnswer.id ? { ...answerVariant, ...updatedAnswer } : answerVariant)
      answerModule.setAnswerVariantList(updatedAnswerVariants)

      const selectedAnswer = answerModule.getAnswer()
      if (answerId === selectedAnswer.id) {
        answerModule.setAnswer({ ...selectedAnswer, published: isPublished })
      }
    } catch (error) {
      if (error.message.includes('empty conversation')) {
        toaster.error(error.message)
      } else {
        toaster.error('Error during publish.', error)
      }
      throw error
    } finally {
      busyButtonsModule.setButtonBusy(publishAnswerToggleId, false)
    }
  }

  async updateAnswerVariantsByPublished(isPublished, answerVariants = []) {
    const toggleIds = answerVariants.map(({ id }) => BUSY_BUTTONS.PUBLISH_ANSWER + id)
    toggleIds.forEach(toggleId => busyButtonsModule.setButtonBusy(toggleId, true))

    try {
      const updatedAnswers = await Promise.all(
        answerVariants.map(answerVariant =>
          answerService.update(answerVariant.botId, answerVariant.id, { published: isPublished })
        )
      )

      const updatedAnswersById = updatedAnswers.reduce((acc, answer) => {
        acc[answer.id] = answer
        return acc
      }, {})

      const answers = answerModule.getAnswerList()
      answerModule.setAnswerList(
        answers.map(answer => updatedAnswersById[answer.id] || answer)
      )

      const selectedAnswer = answerModule.getAnswer()
      const selectedAnswerMultiBotItemId = selectedAnswer?.multiBotItemId || selectedAnswer?.id
      const updatedMultiBotItemId = updatedAnswers[0]?.multiBotItemId || updatedAnswers[0]?.id
      if (selectedAnswerMultiBotItemId === updatedMultiBotItemId) {
        const answerVariantsInStore = answerModule.getAnswerVariantList()
        answerModule.setAnswerVariantList(
          answerVariantsInStore.map(answerVariant => updatedAnswersById[answerVariant.id] || answerVariant)
        )

        const updatedSelectedAnswer = updatedAnswersById[selectedAnswer?.id]
        if (updatedSelectedAnswer) {
          answerModule.setAnswer({ ...selectedAnswer, ...updatedSelectedAnswer })
        }
      }

      return updatedAnswers
    } catch (error) {
      if (error.message.includes('empty conversation')) {
        toaster.error(error.message)
      } else {
        toaster.error('Error during publish.', error)
      }
      throw error
    } finally {
      toggleIds.forEach(toggleId => busyButtonsModule.setButtonBusy(toggleId, false))
    }
  }

  detectPreviewChange(isNewItem) {
    const archivedConversationJson = conversationModule.getArchivedConversationJson()
    const currentConversationJson = conversationModule.getRootJson()

    // Check if selected item is not new answer and conversations empty
    const hasNoConversation = this._isConversationEmpty(currentConversationJson)
    if (!isNewItem && !archivedConversationJson && hasNoConversation) return false

    return !equals(archivedConversationJson, currentConversationJson)
  }

  detectIntentDescriptionChange() {
    const archivedIntentDescription = conversationModule.getArchivedIntentDescription()
    const currentIntentDescription = intentModule.getIntent().description || null
    return archivedIntentDescription !== currentIntentDescription
  }

  /** See docs for 'beforeunload' event https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload */
  onBeforeUnload(e, isNewItem = false) {
    const wasPreviewChanged = this.detectPreviewChange(isNewItem)
    const wasIntentDescriptionChanged = this.detectIntentDescriptionChange()
    if (wasPreviewChanged || wasIntentDescriptionChanged) {
      e.preventDefault()
      e.returnValue = ''
      return
    }

    delete e['returnValue']
  }

  _isConversationEmpty(currentConversationJson) {
    const actions = values(currentConversationJson.actions)
    // for: new answer -> input blurred by click on add message
    const isConvWithEmptyBubble = actions.length === 1 && !actions[0].content?.payload
    const hasNoConversation = isEmpty(currentConversationJson.states)
    return hasNoConversation || isConvWithEmptyBubble
  }

  async createAnswer() {
    const botId = botModule.getBot().id
    const answer = answerModule.getAnswer()
    const conversationJson = conversationModule.getRootJson()
    const newAnswer = await answerService.create(botId, answer.name, conversationJson)
    botModule.setBot(await botService.getBot(botId))
    conversationModule.setId(newAnswer.id)
    conversationModule.setAnswers({ [newAnswer.id]: newAnswer.conversationJson })
    conversationModule.setAnswersHashes({ [newAnswer.id]: newAnswer.conversationJsonHash })

    this.updateRouteByItemId(ITEM_NAMES.ANSWER, { botId, answerId: newAnswer.id })
    return { ...newAnswer, conversationJson: newAnswer.conversationJson }
  }
}

export default new EditorUtils()
